// structuralSlots.js
// Structural Photo Manager storage — one slot per section (see structuralItems.js)
// plus the lead photos and the site systems answers.
//
// Firestore: projects/{projectId}/forms/structuralSlots
//   {
//     siteSystems: { steel: { present: false }, balconies: { present: true }, … },
//     slots: {
//       st_aerial: { photos: ['photoId'] , … },
//       st_elev:   { photos: ['id1', 'id2', 'id3'], … },
//       st_07: {
//         photos:  ['id4', 'id5'],                 // section photos, captioned with the heading
//         answers: { '7a3': 'Roof drains to …', '7a6': 'Fair', '7a6_txt': '…' },
//         repairs: [ { id, ref: '7a', text: 'Replace corroded gutter …', photos: ['id6'] } ],
//         naReason: '', naSource: '',              // engineer marked the section N/A
//         review: 'empty' | 'ai' | 'edited' | 'confirmed', aiNote,
//         confirmedBy, confirmedAt, updatedAt, updatedBy,
//       },
//     }
//   }
// Slots are written by field path (slots.st_07), so one save never overwrites another.

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';
import {
  getStructuralSections, STRUCTURAL_LEAD, effectiveAnswers, lineActive,
  answerNeedsRepair, sectionIsNA,
} from './structuralItems';

const slotsRef = (projectId) => doc(db, 'projects', projectId, 'forms', 'structuralSlots');

// ─── Shape ────────────────────────────────────────────────────────────────────

export const emptyStructuralSlot = () => ({
  photos: [], answers: {}, repairs: [], naReason: '', naSource: '',
  review: 'empty', aiNote: '', confirmedBy: null, confirmedAt: null,
});

const normalize = (raw) => ({
  photos:  Array.isArray(raw?.photos) ? raw.photos.filter(Boolean) : [],
  answers: raw?.answers && typeof raw.answers === 'object' ? { ...raw.answers } : {},
  repairs: Array.isArray(raw?.repairs)
    ? raw.repairs.map(r => ({ id: r?.id || String(Date.now()), ref: r?.ref || '', text: r?.text || '', photos: Array.isArray(r?.photos) ? r.photos.filter(Boolean) : [] }))
    : [],
  naReason:    raw?.naReason || '',
  naSource:    raw?.naSource || '',
  review:      ['empty', 'ai', 'edited', 'confirmed'].includes(raw?.review) ? raw.review : 'empty',
  aiNote:      raw?.aiNote || '',
  confirmedBy: raw?.confirmedBy || null,
  confirmedAt: raw?.confirmedAt || null,
});

export const isConfirmed = (slot) => slot?.review === 'confirmed';

// Any edit un-confirms the slot
const touched = (slot) => ({ ...slot, review: 'edited', confirmedBy: null, confirmedAt: null });

// ─── Load / save ──────────────────────────────────────────────────────────────

export const loadStructural = async (projectId, county) => {
  const snap = await getDoc(slotsRef(projectId));
  const data = snap.exists() ? snap.data() : {};
  const stored = data.slots || {};
  const keys = [...STRUCTURAL_LEAD.map(l => l.key), ...getStructuralSections(county).map(s => s.key)];
  const slots = {};
  keys.forEach(k => { slots[k] = stored[k] ? normalize(stored[k]) : emptyStructuralSlot(); });
  return { siteSystems: data.siteSystems || {}, slots };
};

const writeFields = async (projectId, fields) => {
  try {
    await updateDoc(slotsRef(projectId), fields);
  } catch (e) {
    if (e?.code !== 'not-found') throw e;
    await setDoc(slotsRef(projectId), { slots: {}, siteSystems: {} });
    await updateDoc(slotsRef(projectId), fields);
  }
};

export const saveStructuralSlot = async (projectId, key, slot, uid) => {
  const clean = normalize(slot);
  await writeFields(projectId, { [`slots.${key}`]: { ...clean, updatedAt: serverTimestamp(), updatedBy: uid || null } });
  return clean;
};

export const saveStructuralSystems = async (projectId, siteSystems) => {
  await writeFields(projectId, { siteSystems });
};

// ─── Edits (pure — return a new slot) ─────────────────────────────────────────

export const addPhoto = (slot, photoId) => {
  const s = normalize(slot);
  return touched({ ...s, photos: [...s.photos.filter(id => id !== photoId), photoId] });
};
export const replacePhoto = (slot, index, photoId) => {
  const s = normalize(slot);
  const photos = [...s.photos];
  photos[index] = photoId;
  return touched({ ...s, photos });
};
export const removePhoto = (slot, photoId) => {
  const s = normalize(slot);
  return touched({ ...s, photos: s.photos.filter(id => id !== photoId) });
};
export const movePhoto = (slot, index, dir) => {
  const s = normalize(slot);
  const j = index + dir;
  if (j < 0 || j >= s.photos.length) return s;
  const photos = [...s.photos];
  [photos[index], photos[j]] = [photos[j], photos[index]];
  return touched({ ...s, photos });
};

export const setAnswer = (slot, id, value) => {
  const s = normalize(slot);
  return touched({ ...s, answers: { ...s.answers, [id]: value } });
};

export const addRepair = (slot, ref) => {
  const s = normalize(slot);
  return touched({ ...s, repairs: [...s.repairs, { id: `r${Date.now()}`, ref, text: '', photos: [] }] });
};
export const updateRepair = (slot, repairId, patch) => {
  const s = normalize(slot);
  return touched({ ...s, repairs: s.repairs.map(r => (r.id === repairId ? { ...r, ...patch } : r)) });
};
export const removeRepair = (slot, repairId) => {
  const s = normalize(slot);
  return touched({ ...s, repairs: s.repairs.filter(r => r.id !== repairId) });
};
export const addRepairPhoto = (slot, repairId, photoId) => {
  const s = normalize(slot);
  return touched({ ...s, repairs: s.repairs.map(r => (r.id === repairId ? { ...r, photos: [...r.photos.filter(id => id !== photoId), photoId] } : r)) });
};
export const removeRepairPhoto = (slot, repairId, photoId) => {
  const s = normalize(slot);
  return touched({ ...s, repairs: s.repairs.map(r => (r.id === repairId ? { ...r, photos: r.photos.filter(id => id !== photoId) } : r)) });
};

// Remove a photo wherever it is used in this slot (section photos or any repair)
export const detachPhoto = (slot, photoId) => {
  const s = normalize(slot);
  return touched({
    ...s,
    photos: s.photos.filter(id => id !== photoId),
    repairs: s.repairs.map(r => ({ ...r, photos: r.photos.filter(id => id !== photoId) })),
  });
};

export const setNA = (slot, on) => {
  const s = normalize(slot);
  return touched({ ...s, naReason: on ? 'N/A' : '', naSource: on ? 'engineer' : '' });
};

export const confirmStructural = (slot, uid) => ({
  ...normalize(slot), review: 'confirmed', confirmedBy: uid || null, confirmedAt: new Date().toISOString(),
});

// ─── Completeness ─────────────────────────────────────────────────────────────

const answered = (line, value) => {
  if (value === undefined || value === null) return false;
  return String(value).trim() !== '';
};

// What still blocks Confirm. Empty list → can confirm.
export const structuralProblems = (item, slot, siteSystems) => {
  const s = normalize(slot);
  const problems = [];
  if (item.lead) {
    if (!s.photos.length) problems.push('Choose a photo.');
    return problems;
  }
  if (sectionIsNA(item, s, siteSystems)) return problems;

  if (!s.photos.length) problems.push('Choose at least one photo or mark the section N/A.');

  const { answers, locked } = effectiveAnswers(item, s, siteSystems);
  const missing = item.lines.filter(l => !l.opt && !locked[l.id] && lineActive(l, answers) && !answered(l, answers[l.id]));
  if (missing.length) {
    const refs = [...new Set(missing.map(l => l.id.split('_')[0]))];
    problems.push(`Answer: ${refs.join(', ')}.`);
  }

  if (answerNeedsRepair(item, answers) && s.repairs.length === 0) {
    problems.push('An answer says repairs are required. Add a repair item.');
  }
  s.repairs.forEach((r, i) => {
    if (!(r.text || '').trim()) problems.push(`Repair ${i + 1}: describe the repair.`);
    if (!r.photos.length) problems.push(`Repair ${i + 1}: add at least one repair photo.`);
  });
  return problems;
};

export const slotHasRepairs = (slot) => (slot?.repairs || []).length > 0;
